import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Wifi, Zap, Phone, Plane, ExternalLink, ChevronRight, ShieldCheck, Globe, Loader2 } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { BrandLogo } from "@/components/BrandLogo";
import { usePageMeta } from "@/lib/usePageMeta";
import api from "@/lib/api";

const guessCurrency = () => { try { const r = (navigator.language || "").split("-")[1] || ""; return ({ IN: "inr", US: "usd", GB: "gbp", AE: "aed", SG: "sgd", AU: "aud", CA: "cad" })[r] || "usd"; } catch { return "usd"; } };

const REASSURE = [
  { Icon: Zap, title: "Instant delivery", body: "Your eSIM QR arrives by email in minutes — no shipping, no SIM swap." },
  { Icon: Phone, title: "Keep your number", body: "Data runs on the eSIM while your home number stays reachable on the physical SIM." },
  { Icon: Plane, title: "Activate before you fly", body: "Install at home and switch it on the moment you land — online straight off the plane." },
];

const FAQ = [
  ["Is my phone eSIM-compatible?", "Most phones from 2019 onward support eSIM (iPhone XS+, recent Samsung/Pixel). Each partner lists supported devices before you buy."],
  ["Will I lose my WhatsApp / number?", "No. The travel eSIM only carries data. Your regular number keeps working for calls and texts on your primary SIM."],
  ["How much data do I need?", "Light use (maps, chat) 1–3 GB per week; heavy use (video, hotspot) 5 GB+. Partners show plan sizes for each country so you can pick."],
  ["Can I top up abroad?", "Yes — every partner lets you add more data from the app while you travel, no new QR needed."],
];

const CUR_SYM = { inr: "₹", usd: "$", eur: "€", gbp: "£", aed: "AED ", sgd: "S$", aud: "A$", cad: "C$" };
const symOf = (cur) => CUR_SYM[String(cur || "").toLowerCase()] || (cur ? String(cur).toUpperCase() + " " : "$");

// slug → ISO-2 for flag emojis (regional-indicator pairs). Europe uses the EU flag.
const ISO2 = {
  "united-arab-emirates": "AE", "united-states": "US", "thailand": "TH", "singapore": "SG",
  "united-kingdom": "GB", "europe": "EU", "turkey": "TR", "indonesia": "ID", "malaysia": "MY",
  "vietnam": "VN", "japan": "JP", "south-korea": "KR", "china": "CN", "hong-kong": "HK",
  "india": "IN", "sri-lanka": "LK", "nepal": "NP", "maldives": "MV", "saudi-arabia": "SA",
  "qatar": "QA", "oman": "OM", "france": "FR", "italy": "IT", "spain": "ES", "germany": "DE",
  "switzerland": "CH", "greece": "GR", "netherlands": "NL", "portugal": "PT",
  "united-kingdom-and-ireland": "GB", "australia": "AU", "new-zealand": "NZ", "canada": "CA",
  "mexico": "MX", "brazil": "BR", "egypt": "EG", "south-africa": "ZA", "morocco": "MA",
};
const flagOf = (slug) => {
  const cc = ISO2[slug];
  if (!cc) return "🌐";
  try { return cc.toUpperCase().replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0))); }
  catch { return "🌐"; }
};

const PartnerCard = ({ o }) => (
  <a href={o.url} target="_blank" rel="nofollow sponsored noopener noreferrer" data-testid={`esim-partner-${o.name.toLowerCase()}`}
    className="group flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md">
    <span className="flex min-w-0 items-center gap-3">
      <BrandLogo name={o.name} logo={o.logo} size={40} rounded="rounded-xl" />
      <span className="min-w-0">
        <span className="block text-sm font-bold text-slate-900">{o.name}</span>
        <span className="block truncate text-xs text-slate-500">{o.subtitle}</span>
      </span>
    </span>
    <span className="flex shrink-0 flex-col items-end gap-1">
      {o.price_from != null && (
        <span className="text-right leading-tight" data-testid={`esim-price-${o.name.toLowerCase()}`}>
          <span className="flex items-center justify-end gap-1 text-[10px] font-medium text-slate-400">
            {o.live && <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" data-testid={`esim-live-${o.name.toLowerCase()}`} />}from
          </span>
          <span className="block text-sm font-extrabold text-slate-900">{symOf(o.currency)}{o.price_from}</span>
          {o.plan_label && <span className="block text-[10px] text-slate-400">{o.plan_label}</span>}
        </span>
      )}
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3.5 py-1.5 text-xs font-semibold text-white transition group-hover:bg-emerald-700">
        View plans <ExternalLink className="h-3 w-3" />
      </span>
    </span>
  </a>
);

export default function EsimLandingPage() {
  usePageMeta("Travel eSIM by Country · Suffle Online", "Stay connected abroad with a travel eSIM. Pick your destination and get instant mobile data from trusted partners — Airalo, Yesim, Drimsim.");
  const [catalog, setCatalog] = useState({ countries: [], popular: [], marker: false });
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null); // selected country slug
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [currency, setCurrency] = useState(guessCurrency);

  useEffect(() => { api.get("/services/esim-options").then((r) => { const d = r.data || {}; setCatalog(d); if (d.currency) setCurrency(d.currency); }).catch(() => {}); }, []);

  const pick = async (slug) => {
    setSel(slug); setBusy(true); setData(null);
    try { const r = await api.get(`/services/esim-options?country=${encodeURIComponent(slug)}&currency=${currency}`); setData(r.data); }
    catch { setData(null); }
    setBusy(false);
    setTimeout(() => document.querySelector('[data-testid="esim-results"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  const popular = useMemo(() => (catalog.countries || []).filter((c) => (catalog.popular || []).includes(c.slug)), [catalog]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return catalog.countries || [];
    return (catalog.countries || []).filter((c) => c.name.toLowerCase().includes(t) || c.region.toLowerCase().includes(t));
  }, [q, catalog]);

  return (
    <PublicShell>
      <div data-testid="esim-landing">
        {/* Hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-teal-600 to-sky-700">
          <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
          <div className="relative mx-auto max-w-5xl px-4 py-12 sm:py-16">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/25"><Wifi className="h-3.5 w-3.5" /> Travel eSIM</div>
            <h1 className="mt-4 max-w-2xl text-4xl font-bold leading-tight text-white sm:text-5xl">Stay connected the moment you land</h1>
            <p className="mt-3 max-w-xl text-base text-white/85">Pick your destination and get instant mobile data from trusted partners. No roaming bills, no SIM swap — just scan and go.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {popular.map((c) => (
                <button key={c.slug} onClick={() => pick(c.slug)} data-testid={`esim-popular-${c.slug}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${sel === c.slug ? "bg-white text-emerald-700" : "bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"}`}>
                  <span className="text-base leading-none">{flagOf(c.slug)}</span>{c.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-5xl px-4 py-10">
          {/* Country picker */}
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><Globe className="h-4 w-4 text-emerald-600" /> Where are you travelling?</p>
            <div className="relative mt-3 max-w-md">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a country or region…" data-testid="esim-country-search"
                className="h-11 w-full rounded-full border border-slate-200 pl-9 pr-4 text-sm outline-none focus:border-emerald-400" />
            </div>
            <div className="mt-4 grid max-h-80 grid-cols-2 gap-2.5 overflow-y-auto pr-1 sm:grid-cols-3 md:grid-cols-4" data-testid="esim-country-list">
              {filtered.map((c) => { const on = sel === c.slug; return (
                <button key={c.slug} onClick={() => pick(c.slug)} data-testid={`esim-country-${c.slug}`}
                  className={`group flex items-center gap-2.5 rounded-2xl border p-2.5 text-left transition ${on ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200" : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-sm"}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-xl leading-none ring-1 ring-slate-100 transition group-hover:bg-emerald-50">{flagOf(c.slug)}</span>
                  <span className="min-w-0">
                    <span className={`block truncate text-sm font-bold ${on ? "text-emerald-700" : "text-slate-800"}`}>{c.name}</span>
                    <span className="block truncate text-[10px] font-medium text-slate-400">{c.region}</span>
                  </span>
                </button>
              ); })}
              {filtered.length === 0 && <p className="col-span-full py-4 text-sm text-slate-400" data-testid="esim-no-country">No destination matches "{q}". Try a country or region name.</p>}
            </div>
          </div>

          {/* Results */}
          <div data-testid="esim-results" className="mt-8 scroll-mt-20">
            {busy && <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="esim-loading"><Loader2 className="h-4 w-4 animate-spin" /> Finding eSIM options…</div>}
            {!busy && data && (
              <div>
                <h2 className="text-lg font-bold text-slate-900">eSIMs for {data.country?.name}</h2>
                {data.country?.why && <p className="mt-1 max-w-2xl rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 text-sm leading-relaxed text-slate-600" data-testid="esim-why">{data.country.why}</p>}
                {data.market?.price != null && (
                  <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3.5 py-1.5 text-xs font-bold text-white" data-testid="esim-market-from">
                    <Zap className="h-3.5 w-3.5" /> Live cheapest for {data.country?.name}: from {symOf(data.market.currency || data.currency)}{Math.round(data.market.price)}
                    {data.market.data ? <span className="font-medium opacity-90">· {[data.market.data, data.market.validity].filter(Boolean).join(" · ")}</span> : null}
                  </div>
                )}
                <p className="mt-2 text-sm text-slate-500">Choose a partner to see live plans, data sizes and prices for {data.country?.name} on their site. We don't add markups — you pay the partner's own price.</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="esim-options-grid">
                  {(data.options || []).map((o) => <PartnerCard key={o.name} o={o} />)}
                </div>
                <p className="mt-3 flex flex-wrap items-center gap-1 text-[11px] text-slate-400" data-testid="esim-price-note">
                  Prices in {String(data.currency || "usd").toUpperCase()} — a green dot marks live rates; others are indicative starting levels. Final plans and prices are confirmed on the partner's site.
                  {data.attribution && <a href={data.attribution.url} target="_blank" rel="nofollow noopener noreferrer" className="font-semibold text-emerald-700 hover:underline" data-testid="esim-attribution">{data.attribution.text}</a>}
                </p>
              </div>
            )}
            {!busy && !data && (
              <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-8 text-center text-sm text-slate-400" data-testid="esim-empty-prompt">
                Pick a destination above to see eSIM options.
              </p>
            )}
          </div>

          {/* Reassurance */}
          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {REASSURE.map(({ Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><Icon className="h-5 w-5" /></div>
                <p className="mt-3 text-sm font-bold text-slate-900">{title}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{body}</p>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="mt-12">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Good to know</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {FAQ.map(([qn, an]) => (
                <div key={qn} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="esim-faq-item">
                  <p className="text-sm font-semibold text-slate-800">{qn}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{an}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-slate-50 p-5 text-center">
            <p className="text-sm text-slate-500">Looking for flights, hotels or the rest of your trip?</p>
            <Link to="/services" data-testid="esim-back-to-travel" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-800">Browse all travel <ChevronRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
